import React, { useState, useRef, useEffect } from 'react';
import styles from './Dropdown.module.css';

/**
 * Dropdown Component
 *
 * A reusable dropdown/select component with search and multi-select support
 * following the AB Civil design system.
 *
 * @param {Object} props
 * @param {Array} props.options - Array of {value, label, disabled} objects
 * @param {string|Array} props.value - Selected value(s)
 * @param {function} props.onChange - Change handler (value) => {}
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.searchable - Enable search functionality
 * @param {boolean} props.multiple - Enable multi-select
 * @param {boolean} props.disabled - Disable dropdown
 * @param {boolean} props.loading - Show loading state
 * @param {boolean} props.allowCreate - Allow creating new options
 * @param {function} props.renderOption - Custom option renderer (option) => ReactNode
 * @param {string} props.size - Dropdown size: 'sm' | 'md' | 'lg'
 * @param {string} props.label - Dropdown label
 * @param {string} props.error - Error message
 * @param {string} props.className - Additional CSS classes
 */
const Dropdown = ({
  options = [],
  value = null,
  onChange,
  placeholder = 'Select...',
  searchable = false,
  multiple = false,
  disabled = false,
  loading = false,
  allowCreate = false,
  renderOption = null,
  size = 'md',
  label = null,
  error = null,
  className = '',
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const menuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  // Calculate if dropdown should open upward (smart positioning)
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 300; // Max height of dropdown menu

      // Open upward if not enough space below but more space above
      setOpenUpward(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = searchable && searchTerm
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  // Check if option is selected
  const isSelected = (optionValue) => {
    if (multiple) {
      return Array.isArray(value) && value.includes(optionValue);
    }
    return value === optionValue;
  };

  // Handle option selection
  const handleSelect = (optionValue) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues, optionValue];
      onChange(newValues);
    } else {
      onChange(optionValue);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  // Handle create new option
  const handleCreate = () => {
    if (allowCreate && searchTerm && onChange) {
      if (multiple) {
        onChange([...(Array.isArray(value) ? value : []), searchTerm]);
      } else {
        onChange(searchTerm);
      }
      setSearchTerm('');
      setIsOpen(false);
    }
  };

  // Get display text
  const getDisplayText = () => {
    if (multiple && Array.isArray(value) && value.length > 0) {
      const selectedOptions = options.filter((opt) => value.includes(opt.value));
      return selectedOptions.map((opt) => opt.label).join(', ');
    }
    if (!multiple && value) {
      const selectedOption = options.find((opt) => opt.value === value);
      return selectedOption ? selectedOption.label : placeholder;
    }
    return placeholder;
  };

  const containerClasses = [
    styles.container,
    error && styles.hasError,
    disabled && styles.isDisabled,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const triggerClasses = [
    styles.trigger,
    styles[size],
    isOpen && styles.open,
    error && styles.error,
  ]
    .filter(Boolean)
    .join(' ');

  const displayText = getDisplayText();
  const showPlaceholder = displayText === placeholder;

  return (
    <div className={containerClasses} ref={dropdownRef}>
      {label && (
        <label className={styles.label}>
          {label}
        </label>
      )}

      <button
        type="button"
        className={triggerClasses}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled || loading}
        {...rest}
      >
        <span className={showPlaceholder ? styles.placeholder : styles.value}>
          {displayText}
        </span>
        <svg
          className={`${styles.chevron} ${isOpen && !openUpward ? styles.chevronUp : ''}`}
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div ref={menuRef} className={`${styles.dropdown} ${openUpward ? styles.openUpward : ''}`}>
          {searchable && (
            <div className={styles.searchWrapper}>
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && allowCreate && searchTerm) {
                    e.preventDefault();
                    handleCreate();
                  }
                }}
              />
            </div>
          )}

          <div className={styles.optionsList}>
            {loading ? (
              <div className={styles.loadingState}>Loading...</div>
            ) : filteredOptions.length === 0 ? (
              <div className={styles.emptyState}>
                {searchTerm && allowCreate ? (
                  <button
                    type="button"
                    className={styles.createOption}
                    onClick={handleCreate}
                  >
                    Create "{searchTerm}"
                  </button>
                ) : (
                  'No options found'
                )}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const selected = isSelected(option.value);
                const optionClasses = [
                  styles.option,
                  selected && styles.selected,
                  option.disabled && styles.disabled,
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <button
                    key={option.value}
                    type="button"
                    className={optionClasses}
                    onClick={() => !option.disabled && handleSelect(option.value)}
                    disabled={option.disabled}
                  >
                    {multiple && (
                      <span className={styles.checkbox}>
                        {selected && (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 3L4.5 8.5L2 6"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                    )}
                    {renderOption ? renderOption(option) : option.label}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {error && <p className={styles.errorText}>{error}</p>}
    </div>
  );
};

export default Dropdown;
